import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { List } from 'immutable';
import { compact } from 'lodash';
import { concat, Observable, of } from 'rxjs';
import { v4 } from 'uuid';
import { ActionsEnum as AuthActionsEnum } from '../auth-data/auth-data.actions';
import { UPDATE_FEATURE_FRAMEWORK_DATA_DELAY } from '../features-data/features-data.data';
import { FeaturesDataDispatcher } from '../features-data/features-data.dispatcher';
import {
  ActionsEnum,
  AddAccountUser,
  addAccountUser,
  AddAccountUserNetwork,
  AddAccountUserNetworkError,
  addAccountUserNetworkError,
  AddAccountUserNetworkSuccess,
  addAccountUserNetworkSuccess,
  DeleteAccountUser,
  deleteAccountUser,
  DeleteAccountUserNetwork,
  DeleteAccountUserNetworkError,
  deleteAccountUserNetworkError,
  DeleteAccountUserNetworkSuccess,
  deleteAccountUserNetworkSuccess,
  fetchAccountUsers,
  FetchAccountUsers,
  FetchAccountUsersError,
  fetchAccountUsersError,
  FetchAccountUsersSuccess,
  fetchAccountUsersSuccess,
  updateAccountUser,
  UpdateAccountUser,
  UpdateAccountUserNetwork,
  UpdateAccountUserNetworkError,
  updateAccountUserNetworkError,
  UpdateAccountUserNetworkSuccess,
  updateAccountUserNetworkSuccess,
  UpdateAccountUsers,
  updateAccountUsers,
} from './account-user-data.actions';
import { AccountUserDataService } from './account-user-data.service';
import { AccountUser } from './account-user.model';
import {
  catchError,
  delay,
  flatMap,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';

@Injectable()
export class AccountUserDataEffects {
  constructor(
    private _actions: Actions,
    private _store: Store<any>,
    private _featuresDataDispatcher: FeaturesDataDispatcher,
    private _accountUserDataService: AccountUserDataService
  ) {}

  @Effect()
  onInit: Observable<FetchAccountUsers> = this._actions.pipe(
    ofType(AuthActionsEnum.AUTHENTICATED),
    take(1),
    map(() => fetchAccountUsers(v4()))
  );

  @Effect()
  getAccountUsers: Observable<
    UpdateAccountUsers | FetchAccountUsersSuccess | FetchAccountUsersError
  > = this._actions.pipe(
    ofType(ActionsEnum.FETCH_ACCOUNT_USERS),
    switchMap(({ payload }: FetchAccountUsers) => {
      const { dispatchId } = payload;

      return this._accountUserDataService.getAccountUsers().pipe(
        flatMap((accountUsers: List<AccountUser>) =>
          concat([
            updateAccountUsers(accountUsers),
            fetchAccountUsersSuccess(dispatchId),
          ])
        ),
        catchError((error) => of(fetchAccountUsersError(dispatchId, error)))
      );
    })
  );

  @Effect()
  addAccountUser: Observable<
    | AddAccountUserNetworkSuccess
    | DeleteAccountUser
    | AddAccountUserNetworkError
  > = this._actions.pipe(
    ofType(ActionsEnum.ADD_ACCOUNT_USER_NETWORK),
    flatMap(({ payload }: AddAccountUserNetwork) => {
      const { accountUser, dispatchId } = payload;

      return this._accountUserDataService.addAccountUser(accountUser).pipe(
        map((accountUser: AccountUser) =>
          addAccountUserNetworkSuccess(dispatchId, accountUser)
        ),
        catchError((error) =>
          concat(
            compact([
              accountUser ? deleteAccountUser(accountUser) : null,
              addAccountUserNetworkError(dispatchId, accountUser, error),
            ])
          )
        )
      );
    })
  );

  @Effect()
  updateAccountUser: Observable<
    | UpdateAccountUserNetworkSuccess
    | UpdateAccountUserNetworkError
    | UpdateAccountUser
  > = this._actions.pipe(
    ofType(ActionsEnum.UPDATE_ACCOUNT_USER_NETWORK),
    flatMap(({ payload }: UpdateAccountUserNetwork) => {
      const { prevAccountUser, currentAccountUser, dispatchId } = payload;

      return this._accountUserDataService
        .updateAccountUser(currentAccountUser)
        .pipe(
          map((accountUser: AccountUser) =>
            updateAccountUserNetworkSuccess(dispatchId, accountUser)
          ),
          catchError((error) =>
            concat(
              compact([
                prevAccountUser ? updateAccountUser(prevAccountUser) : null,
                updateAccountUserNetworkError(
                  dispatchId,
                  prevAccountUser,
                  currentAccountUser,
                  error
                ),
              ])
            )
          )
        );
    })
  );

  @Effect()
  deleteAccountUser: Observable<
    | DeleteAccountUserNetworkSuccess
    | DeleteAccountUserNetworkError
    | AddAccountUser
  > = this._actions.pipe(
    ofType(ActionsEnum.DELETE_ACCOUNT_USER_NETWORK),
    flatMap(({ payload }: DeleteAccountUserNetwork) => {
      const { accountUser, dispatchId } = payload;

      return this._accountUserDataService
        .deleteAccountUser(accountUser.userId)
        .pipe(
          map(() => deleteAccountUserNetworkSuccess(dispatchId, accountUser)),
          catchError((error) =>
            concat(
              compact([
                accountUser ? addAccountUser(accountUser) : null,
                deleteAccountUserNetworkError(dispatchId, accountUser, error),
              ])
            )
          )
        );
    })
  );

  @Effect({ dispatch: false })
  updateQuotas: Observable<any> = this._actions.pipe(
    ofType(
      ActionsEnum.ADD_ACCOUNT_USER_NETWORK_SUCCESS,
      ActionsEnum.DELETE_ACCOUNT_USER_NETWORK_SUCCESS
    ),
    switchMap(() =>
      of(void 0).pipe(
        delay(UPDATE_FEATURE_FRAMEWORK_DATA_DELAY),
        tap(() => this._featuresDataDispatcher.fetchQuotaBannersCanvases())
      )
    )
  );
}
